import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Heart } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT_INFO } from "../lib/data";

export default function About() {
  const { t } = useLang();
  return (
    <div data-testid="page-about">
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-4">
                <span className="h-px w-8 bg-[#0022FF]" />
                {t.nav.about}
              </div>
              <h1 className="font-display font-black text-slate-900 text-5xl sm:text-6xl lg:text-7xl tracking-tight">
                {t.about.title}
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.about.subtitle}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square rounded-[2rem] overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/35290675/pexels-photo-35290675.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
                  alt="SHK Team"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0022FF] text-white rounded-2xl p-6 shadow-xl">
                <div className="font-display font-black text-4xl">15+</div>
                <div className="text-xs uppercase tracking-wider text-sky-200">
                  {t.home.statYears}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-4">
            — {t.about.story}
          </div>
          <h2 className="font-display font-black text-slate-900 text-4xl sm:text-5xl tracking-tight">
            {t.about.story}
          </h2>
          <p className="mt-6 text-lg text-slate-700 leading-[1.8]">{t.about.storyText}</p>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-4">
              — {t.about.values}
            </div>
            <h2 className="font-display font-black text-slate-900 text-4xl sm:text-5xl tracking-tight">
              {t.about.values}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Award, title: t.about.value1Title, text: t.about.value1Text },
              { icon: ShieldCheck, title: t.about.value2Title, text: t.about.value2Text },
              { icon: Heart, title: t.about.value3Title, text: t.about.value3Text },
            ].map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-3xl bg-white border border-slate-200 p-8 hover:shadow-xl transition-shadow"
              >
                <div className="h-14 w-14 rounded-2xl bg-[#0022FF] text-white flex items-center justify-center mb-5">
                  <v.icon className="h-7 w-7" />
                </div>
                <h3 className="font-display font-bold text-2xl text-slate-900 mb-3 tracking-tight">
                  {v.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team / Founder */}
      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#0022FF] to-[#4D6BFF] text-white p-10 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 grain-texture opacity-40" />
            <div className="relative">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-200 mb-4">
                — {t.about.team}
              </div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight">
                {t.about.founderName}
              </h2>
              <p className="mt-2 text-sky-200 text-lg">{t.about.founderRole}</p>
              <p className="mt-6 text-sky-50/90 leading-relaxed max-w-2xl">{t.about.founderBio}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/kontakt"
                  data-testid="about-cta-contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-[#0022FF] px-6 py-3 text-sm font-semibold hover:bg-sky-50"
                >
                  {t.nav.contact}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-6 py-3 text-sm font-semibold hover:bg-white/10"
                >
                  {CONTACT_INFO.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
